import {
  Table,
  Thead,
  Tr,
  Th,
  Box,
  Tbody,
  Td,
  Text,
  Flex,
  Tooltip,
  useColorModeValue,
  HStack,
  Button,
  useToast,
  IconButton,
  Select,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Badge,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { uploadFinancialReport, getFinancialReportsList, getFinancialReportReceipts, uploadFinancialProofReport } from "../../../services/consignado/financial-report.service";
import { FaFileExcel, FaFileUpload } from "react-icons/fa";
import { MdViewAgenda, MdViewColumn } from "react-icons/md";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "@chakra-ui/icons";
import { FinancialReportListItem } from "@/services/consignado/dto/financial-reports.dto";

export default function FinancialReport({
  backgroundStyle,
}: any) {
  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");
  const [financialReportsList, setFinancialReportsList] = useState<FinancialReportListItem[]>([])
  const [isLoading, setIsLoading] = useState(false);
  const fileInputInsuranceRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const proofFileInputRef = useRef<HTMLInputElement>(null);
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false)
  const toast = useToast();

  // Estados de paginação
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Estados para modal de comprovantes
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [proofDetails, setProofDetails] = useState<any[]>([]);
  const [financialAggregateSelected, setFinancialAggregateSelected] = useState<string>('');
  const [selectedFinancialReportId, setSelectedFinancialReportId] = useState<string | null>(null);
  const fetchFinancialReportsList = async () => {
    setIsLoading(true);
    try {
      const skip = (page - 1) * pageSize;

      const response = await getFinancialReportsList({
        take: String(pageSize),
        skip: String(skip)
      });

      if (response && response.data) {
        setFinancialReportsList(response.data);
        setTotalPages(response.meta?.totalPages || 1);
      } else {
        setFinancialReportsList([]);
        setTotalPages(1);
      }

      if (shouldRefetch) {
        setShouldRefetch(false);
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar comprovantes",
        description: error?.message || "Ocorreu um erro ao buscar os dados",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setFinancialReportsList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialReportsList();
  }, [page, pageSize, shouldRefetch]);

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; colorScheme: string } } = {
      SUCCESS: { label: "Sucesso", colorScheme: "green" },
      ERROR: { label: "Erro", colorScheme: "red" },
      PENDING: { label: "Pendente", colorScheme: "yellow" },
    };

    const statusInfo = statusMap[status] || { label: status, colorScheme: "gray" };

    return (
      <Badge colorScheme={statusInfo.colorScheme} fontSize="0.8em" px={2} py={1}>
        {statusInfo.label}
      </Badge>
    );
  };

  const handleProofSelectFile = (id?: string | null) => {
    if (!id) {
      toast({ title: "Erro", description: "ID do relatório financeiro não informado", status: "error", duration: 5000, isClosable: true });
      return;
    }
    setSelectedFinancialReportId(id);
    proofFileInputRef.current?.click();
  };

  const handleGetProof = async (id: string) => {
    try {
      setOpenModal(true);
      setFinancialAggregateSelected(id);
      const receipts = await getFinancialReportReceipts(id);
      setProofDetails(receipts);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar comprovantes",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setProofDetails([]);
    }
  };

  const handleSelectInsuranceFile = () => {
    fileInputInsuranceRef.current?.click();
  }

  const sendInsuranceReportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validar se é arquivo xlsx
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const isValidType = validTypes.includes(file.type);
    const isValidName = file.name.endsWith('.xlsx');

    if (!isValidType && !isValidName) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo Excel (.xlsx)",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await uploadFinancialReport(file);
      toast({
        title: "Sucesso",
        description: "Arquivo enviado e processado com sucesso!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Recarregar dados após upload
      setShouldRefetch(true)
    } catch (error: any) {
      const errorMessage = error?.response?.data?.menssagem_erro;
      const isAlreadyProcessed = errorMessage === "Arquivo já processado anteriormente";

      toast({
        title: isAlreadyProcessed ? "Aviso" : "Erro",
        description: isAlreadyProcessed
          ? "Este arquivo já foi importado anteriormente. Por favor, selecione um arquivo diferente."
          : errorMessage || "Erro ao processar o arquivo",
        status: isAlreadyProcessed ? "warning" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      // Limpar o input
      if (fileInputInsuranceRef.current) {
        fileInputInsuranceRef.current.value = '';
      }
    }
  }

  const sendProcessorReportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validar se é arquivo xlsx
    const isValidName = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (!isValidName) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo Excel (.xlsx)",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await uploadFinancialReport(file);
      toast({
        title: "Sucesso",
        description: "Arquivo enviado e processado com sucesso!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Recarregar dados após upload
      setShouldRefetch(true)
    } catch (error: any) {
      const errorMessage = error?.response?.data?.menssagem_erro;
      const isAlreadyProcessed = errorMessage === "Arquivo já processado anteriormente";

      toast({
        title: isAlreadyProcessed ? "Aviso" : "Erro",
        description: isAlreadyProcessed
          ? "Este arquivo já foi importado anteriormente. Por favor, selecione um arquivo diferente."
          : errorMessage || "Erro ao processar o arquivo",
        status: isAlreadyProcessed ? "warning" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      // Limpar o input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }
  const sendProofPaymentFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];

      if (!file) return;
      if (!selectedFinancialReportId) {
        toast({ title: "Erro", description: "ID do relatório financeiro não informado", status: "error", duration: 5000, isClosable: true });
        return;
      }

      const validTypes = ['application/pdf'];
      const isValidType = validTypes.includes(file.type);

      if (!isValidType) {
        toast({
          title: "Erro",
          description: "Por favor, selecione um arquivo PDF",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      await uploadFinancialProofReport(file, selectedFinancialReportId);
      toast({ title: "Sucesso", description: "Comprovante enviado e processado com sucesso!", status: "success", duration: 5000, isClosable: true });

      // Recarregar dados após upload
      setShouldRefetch(true)

    } catch (error: any) {
      const errorMessage = error?.response?.data?.menssagem_erro;
      const isAlreadyProcessed = errorMessage === "Arquivo já processado anteriormente";

      toast({
        title: isAlreadyProcessed ? "Aviso" : "Erro",
        description: isAlreadyProcessed
          ? "Este arquivo já foi importado anteriormente. Por favor, selecione um arquivo diferente."
          : errorMessage || "Erro ao processar o arquivo",
        status: isAlreadyProcessed ? "warning" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSelectedFinancialReportId(null);
      if (proofFileInputRef.current) {
        proofFileInputRef.current.value = '';
      }
    }

  }

  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        width="100%"
        mb={4}
      >
        <Text fontSize="xl" fontWeight="bold" fontFamily="Lexend Deca" mb={4}>
          Importação de dados da seguradora ou bancarizadora
        </Text>
        <Flex gap={4} justifyContent="space-between" flexWrap="wrap">
          <Flex gap={4} justifyContent="flex-end">
            <input
              ref={fileInputInsuranceRef}
              type="file"
              accept=".xlsx"
              onChange={sendInsuranceReportFile}
              style={{ display: 'none' }}
            />
            <Button
              color="white"
              colorScheme="green"
              textTransform="uppercase"
              onClick={handleSelectInsuranceFile}
              isLoading={isLoading}
            >
              Selecionar arquivo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={sendProcessorReportFile}
              style={{ display: 'none' }}
            />
            <input
              ref={proofFileInputRef}
              type="file"
              accept="application/pdf"
              onChange={sendProofPaymentFile}
              style={{ display: 'none' }}
            />
          </Flex>
        </Flex>
      </Box>

      <Box overflow="auto" borderRadius="md" boxShadow="md" width="100%">
        <Table w={"100%"} size="sm">
          <Thead
            {...backgroundStyle}
            position="sticky"
            top={0}
            zIndex={1}
            boxShadow="md"
          >
            <Tr>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Nome</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Data início</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Data fim</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Status</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Total Sucesso</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Total Erros</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Nome Provedor</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                <HStack justify="center">
                  <Text>Data criação</Text>
                </HStack>
              </Th>
              <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                Ações
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={9} textAlign="center" py={8}>
                  <Text>Carregando...</Text>
                </Td>
              </Tr>
            ) : financialReportsList.length === 0 ? (
              <Tr>
                <Td colSpan={9} textAlign="center" py={8}>
                  <Text>Nenhum comprovante encontrado</Text>
                </Td>
              </Tr>
            ) : (
              financialReportsList.map((report, index) => (
                <Tr
                  key={index}
                  _hover={{ bg: rowHoverBg }}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  borderColor={rowBorderColor}
                >
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.name || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.start_date ? new Date(report.start_date).toLocaleDateString('pt-BR') : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.end_date ? new Date(report.end_date).toLocaleDateString('pt-BR') : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.status ? getStatusBadge(report.status) : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.countSuccess ?? "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.countError ?? "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.providerName === "UNKNOWN" ? "Desconhecido" : report.providerName || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {report.createdAt ? new Date(report.createdAt).toLocaleDateString('pt-BR') : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    <HStack justify="center" spacing={4}>
                      <Tooltip label="Importar comprovantes" aria-label="Visualizar Tooltip" placement="top">
                        <Flex
                          justify="center"
                          align="center"
                          cursor="pointer"
                          onClick={() => handleProofSelectFile(report.id)}
                        >
                          <FaFileUpload fontSize="1.5rem" color="teal.500" />
                        </Flex>
                      </Tooltip>

                      <Tooltip label="Baixar arquivo excel" aria-label="Visualizar Tooltip" placement="top">
                        <Flex
                          justify="center"
                          align="center"
                          cursor="pointer"
                          onClick={() => window.open(report.url_storage, '_blank')}
                        >
                          <FaFileExcel fontSize="1.5rem" color="teal.500" />
                        </Flex>
                      </Tooltip>

                      <Tooltip label="Baixar comprovantes" aria-label="Visualizar Tooltip" placement="top">
                        <Flex
                          justify="center"
                          align="center"
                          cursor="pointer"
                          onClick={() => handleGetProof(report.id)}
                        >
                          {(openModal && financialAggregateSelected === report.id) ? (
                            <MdViewColumn fontSize="1.5rem" color="teal.500" />
                          ) : (
                            <MdViewAgenda fontSize="1.5rem" color="teal.500" />
                          )}
                        </Flex>
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {financialReportsList.length > 0 && (
        <Flex
          justifyContent="center"
          mt={4}
          mb={4}
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          width="100%"
        >
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            aria-label="Página Anterior"
          />
          <Text mx={4} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            aria-label="Próxima Página"
          />
          <Flex alignItems="center" ml={4}>
            <Text mr={2} fontFamily="Lexend Deca">
              Itens por página:
            </Text>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              width="fit-content"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </Flex>
        </Flex>
      )}

      <Modal isOpen={openModal} onClose={() => setOpenModal(false)} isCentered>
        <ModalContent>
          <ModalHeader>
            <Flex justifyContent='space-between' alignItems='center'>
              <Text>
                Comprovantes
              </Text>
              <Button size='xs' onClick={() => setOpenModal(false)}>
                <CloseIcon boxSize='.7rem' />
              </Button>
            </Flex>
          </ModalHeader>
          <ModalBody pb={4}>
            <Flex gap={4} wrap="wrap">
              {proofDetails.length > 0 ? proofDetails.map((proof, index) => (
                <Button
                  key={index}
                  size='sm'
                  colorScheme="primary"
                  onClick={() => window.open(proof, '_blank')}
                >
                  Baixar Comprovante {index + 1}
                </Button>
              )) : (
                <Text>
                  Comprovantes não encontrados!
                </Text>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}